import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { TitleProps, Title } from "./Title";
import { SimpleRichTextProps, SimpleRichText } from "./SimpleRichText";
import { ChevronIcon } from "./ChevronIcon";
import { ImageWithAspectRatio } from "./ImageWithAspectRatio";
import { IconProps, ImageWithAspectRatioProps } from "./types";
import { Link } from "./Link";

export const cardWithLink = tv({
  base: cn("uipm:flex uipm:h-full uipm:font-normal"),
  variants: {
    layout: {
      wide: "uipm:justify-between uipm:items-center uipm:gap-4",
      link: cn(
        "uipm:items-start uipm:gap-6",
        "uipm:rounded-none uipm:md:rounded-[var(--radius-lg)]",
      ),
    },
    backgroundColor: {
      primary: "uipm:bg-[var(--surface-surface-accent)]",
      secondary: "uipm:bg-[var(--surface-surface-accent-secondary)]",
      "secondary-light": "uipm:bg-[var(--surface-surface-accent-secondary)]",
      light: "uipm:bg-[var(--surface-surface-secondary)]",
      "very-light": "uipm:bg-[var(--surface-surface-secondary)]",
      white: "uipm:bg-[var(--surface-surface-primary)]",
      none: "uipm:bg-transparent",
    },
    hasLink: {
      true: "uipm:cursor-pointer",
      false: "uipm:cursor-default",
    },
    verticalPadding: {
      default: "uipm:py-8",
      small: "uipm:py-[var(--container-card-padding)]",
      medium: "uipm:py-[var(--container-card-medium-padding)]",
      large: "uipm:py-[var(--container-card-large-padding)]",
    },
    horizontalPadding: {
      default: "uipm:px-4 uipm:md:px-8",
      small: "uipm:px-[var(--container-card-padding)]",
      medium: "uipm:px-[var(--container-card-medium-padding)]",
      large: "uipm:px-[var(--container-card-large-padding)]",
    },
  },
  compoundVariants: [
    {
      layout: "link",
      verticalPadding: "default",
      horizontalPadding: "default",
      className: "uipm:p-[var(--container-card-padding)]",
    },
  ],
  defaultVariants: {
    layout: "wide",
    backgroundColor: "primary",
    hasLink: false,
    verticalPadding: "default",
    horizontalPadding: "default",
  },
});

const titleWrapper = tv({
  base: "uipm:font-normal",
  variants: {
    backgroundColor: {
      primary: "uipm:text-[var(--typography-colors-text-inverted)]",
      secondary: "uipm:text-[var(--typography-colors-text-heading)]",
      "secondary-light": "uipm:text-[var(--typography-colors-text-heading)]",
      light: "uipm:text-[var(--typography-colors-text-heading)]",
      "very-light": "uipm:text-[var(--typography-colors-text-heading)]",
      white: "uipm:text-[var(--typography-colors-text-heading)]",
      none: "uipm:text-[var(--typography-colors-text-heading)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
  },
});

const textWrapper = tv({
  base: "uipm:font-normal",
  variants: {
    backgroundColor: {
      primary: "uipm:text-[var(--typography-colors-text-inverted-faded)]",
      secondary: "uipm:text-[var(--typography-colors-text-accent)]",
      "secondary-light": "uipm:text-[var(--typography-colors-text-accent)]",
      light: "uipm:text-[var(--typography-colors-text-accent)]",
      "very-light": "uipm:text-[var(--typography-colors-text-accent)]",
      white: "uipm:text-[var(--typography-colors-text-faded-dark)]",
      none: "uipm:text-[var(--typography-colors-text-faded-dark)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
  },
});

export interface CardWithLinkProps
  extends Omit<ComponentProps<"div">, "title"> {
  title?: TitleProps | null;
  text?: SimpleRichTextProps | null;
  icon?: ImageWithAspectRatioProps | null;
  href?: string;
  target?: string;
  noFollow?: boolean;
  backgroundColor?:
    | "primary"
    | "secondary"
    | "secondary-light"
    | "light"
    | "very-light"
    | "white"
    | "none";
  layout?: "wide" | "link";
  verticalPadding?: "default" | "small" | "medium" | "large";
  horizontalPadding?: "default" | "small" | "medium" | "large";
  onCardClick?: () => void;
}

export const CardWithLink = React.forwardRef<HTMLDivElement, CardWithLinkProps>(
  (
    {
      title,
      text,
      icon,
      href,
      target,
      noFollow,
      backgroundColor = "primary",
      layout = "wide",
      verticalPadding = "default",
      horizontalPadding = "default",
      onCardClick,
      className,
      ...props
    },
    ref,
  ) => {
    const hasLink = Boolean(href);
    const chevronColor = titleWrapper({ backgroundColor });

    const renderWideLayout = () => (
      <>
        <div className="uipm:flex uipm:flex-col uipm:gap-2 uipm:flex-1 uipm:font-normal">
          <div className="uipm:flex uipm:items-center uipm:gap-4">
            {icon && (
              <div
                className={cn(
                  "uipm:w-8 uipm:h-8 uipm:flex-shrink-0",
                  "uipm:md:flex uipm:md:w-[54px] uipm:md:h-[54px]",
                )}
              >
                <ImageWithAspectRatio
                  {...icon}
                  sizes="(max-width: 768px) 32px, 54px"
                />
              </div>
            )}
            {title && <Title {...title} className={title.className} />}
          </div>
          {text && (
            <div className={textWrapper({ backgroundColor })}>
              <SimpleRichText {...text} />
            </div>
          )}
        </div>
        {hasLink && (
          <div className="uipm:flex uipm:items-center uipm:flex-shrink-0 uipm:ml-4">
            <ChevronIcon className={chevronColor} size={24} />
          </div>
        )}
      </>
    );

    const renderLinkLayout = () => (
      <>
        {/* Part 1: Icon aligned to top left */}
        {icon && (
          <div className="uipm:w-[50px] uipm:h-[50px] uipm:flex-shrink-0 uipm:self-start">
            <ImageWithAspectRatio {...icon} sizes="50px" />
          </div>
        )}

        {/* Part 2: Title + Text one under another without distance */}
        <div className="uipm:flex uipm:flex-col uipm:flex-1 uipm:font-normal">
          {title && <Title {...title} className={title.className} />}
          {text && (
            <div className={textWrapper({ backgroundColor })}>
              <SimpleRichText {...text} />
            </div>
          )}
        </div>

        {/* Part 3: Chevron */}
        {hasLink && (
          <div className="uipm:flex uipm:items-center uipm:justify-center uipm:flex-shrink-0 uipm:self-center">
            <ChevronIcon className={chevronColor} size={24} />
          </div>
        )}
      </>
    );

    const cardContent =
      layout === "link" ? renderLinkLayout() : renderWideLayout();

    if (hasLink) {
      return (
        <Link
          href={href || ""}
          target={target}
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={cn(
            cardWithLink({
              backgroundColor,
              hasLink,
              layout,
              verticalPadding,
              horizontalPadding,
            }),
            className,
          )}
          noFollow={noFollow}
          onClick={onCardClick}
        >
          {cardContent}
        </Link>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          cardWithLink({
            backgroundColor,
            hasLink,
            layout,
            verticalPadding,
            horizontalPadding,
          }),
          className,
        )}
        onClick={onCardClick}
        {...props}
      >
        {cardContent}
      </div>
    );
  },
);

CardWithLink.displayName = "CardWithLink";

export type CardWithLinkVariants = VariantProps<typeof cardWithLink>;
